import type { ReactNode, SVGProps } from 'react'

/**
 * Sidebar navigation icons.
 *
 * Inline SVG rather than an icon package: no icon library is part of the
 * approved stack (CLAUDE.md §30), and inline paths keep the nav out of the
 * critical-path bundle cost of a dependency. Every icon inherits `currentColor`
 * so the active/inactive colours come from tokens on the row, not from here.
 */
export type NavIconProps = SVGProps<SVGSVGElement>

export type NavIcon = (props: NavIconProps) => ReactNode

function Glyph({ children, ...rest }: NavIconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.9}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      {children}
    </svg>
  )
}

/** Dashboard — four panes. */
export function GaugeIcon(props: NavIconProps) {
  return (
    <Glyph {...props}>
      <rect x="3.5" y="3.5" width="7" height="7" rx="1.6" />
      <rect x="13.5" y="3.5" width="7" height="7" rx="1.6" />
      <rect x="3.5" y="13.5" width="7" height="7" rx="1.6" />
      <rect x="13.5" y="13.5" width="7" height="7" rx="1.6" />
    </Glyph>
  )
}

/** Customers — people. */
export function UsersIcon(props: NavIconProps) {
  return (
    <Glyph {...props}>
      <circle cx="9.2" cy="8" r="3.2" />
      <path d="M3.4 19.6c0-3.3 2.6-5.2 5.8-5.2s5.8 1.9 5.8 5.2" />
      <path d="M17 5.2a3.2 3.2 0 0 1 0 6" />
      <path d="M18.4 14.8c1.6.7 2.6 2.2 2.6 4.4" />
    </Glyph>
  )
}

/** Events — flag. */
export function TicketIcon(props: NavIconProps) {
  return (
    <Glyph {...props}>
      <path d="M6 21V4" />
      <path d="M6 4.5h11.5l-2.2 4.2 2.2 4.2H6" />
    </Glyph>
  )
}

/** Hotels — building. */
export function ClocheIcon(props: NavIconProps) {
  return (
    <Glyph {...props}>
      <rect x="4.5" y="3.5" width="15" height="17" rx="1.8" />
      <path d="M9 8h2M13 8h2M9 12h2M13 12h2" />
      <path d="M10 20.5v-3.6h4v3.6" />
    </Glyph>
  )
}

/** Rooms — a bed, for room inventory. */
export function BedIcon(props: NavIconProps) {
  return (
    <Glyph {...props}>
      <path d="M3.5 19.5V6" />
      <path d="M3.5 11.5h17V19.5" />
      <path d="M3.5 15.5h17" />
      <path d="M7.5 11.5V9h4.2v2.5" />
    </Glyph>
  )
}

/** Reservations — calendar with a check. */
export function CheckSquareIcon(props: NavIconProps) {
  return (
    <Glyph {...props}>
      <rect x="3.5" y="5" width="17" height="15.5" rx="2" />
      <path d="M3.5 9.6h17" />
      <path d="M8 5V2.8M16 5V2.8" />
      <path d="m9.2 14.6 2.2 2.2 3.6-4" />
    </Glyph>
  )
}

/** Wait List — clock. */
export function ClockArrowIcon(props: NavIconProps) {
  return (
    <Glyph {...props}>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7.4V12l3.2 2" />
    </Glyph>
  )
}

/** Transactions — card. */
export function BanknoteIcon(props: NavIconProps) {
  return (
    <Glyph {...props}>
      <rect x="2.5" y="5.5" width="19" height="13" rx="2" />
      <path d="M2.5 10h19" />
      <path d="M6 14.4h3" />
    </Glyph>
  )
}

/** Commission — percentage. */
export function PercentIcon(props: NavIconProps) {
  return (
    <Glyph {...props}>
      <path d="M18.8 5.2 5.2 18.8" />
      <circle cx="7.6" cy="7.6" r="2.6" />
      <circle cx="16.4" cy="16.4" r="2.6" />
    </Glyph>
  )
}

/** Reports — bar chart. */
export function FileTextIcon(props: NavIconProps) {
  return (
    <Glyph {...props}>
      <path d="M5.5 20V13" />
      <path d="M10.5 20V6.5" />
      <path d="M15.5 20v-9" />
      <path d="M20.5 20V4" />
    </Glyph>
  )
}

/** Website Content — globe. */
export function GlobeIcon(props: NavIconProps) {
  return (
    <Glyph {...props}>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M3.5 12h17" />
      <path d="M12 3.5c2.4 2.4 3.7 5.3 3.7 8.5S14.4 18.1 12 20.5C9.6 18.1 8.3 15.2 8.3 12S9.6 5.9 12 3.5Z" />
    </Glyph>
  )
}

/** Partners — interlocking rings. */
export function RingsIcon(props: NavIconProps) {
  return (
    <Glyph {...props}>
      <circle cx="9" cy="12" r="5" />
      <circle cx="15" cy="12" r="5" />
    </Glyph>
  )
}

/** Users & Roles — person with settings. */
export function UserCogIcon(props: NavIconProps) {
  return (
    <Glyph {...props}>
      <circle cx="10" cy="7.6" r="3" />
      <path d="M3.6 19.4c0-3.4 2.8-5.2 6.4-5.2.9 0 1.8.1 2.6.4" />
      <circle cx="17.4" cy="17.4" r="2.6" />
      <path d="M17.4 13.2v1.2M17.4 20.4v1.2M13.2 17.4h1.2M20.4 17.4h1.2" />
    </Glyph>
  )
}

/** Global Settings — gear. */
export function GearIcon(props: NavIconProps) {
  return (
    <Glyph {...props}>
      <circle cx="12" cy="12" r="3.2" />
      <path d="M12 2.8v2.4M12 18.8v2.4M5.4 5.4l1.7 1.7M16.9 16.9l1.7 1.7M2.8 12h2.4M18.8 12h2.4M5.4 18.6l1.7-1.7M16.9 7.1l1.7-1.7" />
    </Glyph>
  )
}

/** Secure Hotel Portal — a shield with a check. */
export function ShieldCheckIcon(props: NavIconProps) {
  return (
    <Glyph {...props}>
      <path d="M12 3.2 5 5.9v5.6c0 4.4 2.9 7.9 7 9.3 4.1-1.4 7-4.9 7-9.3V5.9l-7-2.7Z" />
      <path d="m9 12.1 2.1 2.1 4-4.1" />
    </Glyph>
  )
}

/** Audit — clipboard trail. */
export function ClipboardListIcon(props: NavIconProps) {
  return (
    <Glyph {...props}>
      <path d="M9.2 4.4H7.5A1.5 1.5 0 0 0 6 5.9v13.6A1.5 1.5 0 0 0 7.5 21h9a1.5 1.5 0 0 0 1.5-1.5V5.9a1.5 1.5 0 0 0-1.5-1.5h-1.7" />
      <rect x="9.2" y="3" width="5.6" height="2.8" rx="0.9" />
      <path d="M9 10.4h6M9 13.8h6M9 17.2h4" />
    </Glyph>
  )
}

/** Topbar — jump to the dashboard. */
export function HomeIcon(props: NavIconProps) {
  return (
    <Glyph {...props}>
      <path d="M3.5 10.6 12 3.4l8.5 7.2" />
      <path d="M5.6 9.4V20a.8.8 0 0 0 .8.8h11.2a.8.8 0 0 0 .8-.8V9.4" />
      <path d="M9.8 20.8v-6.4h4.4v6.4" />
    </Glyph>
  )
}

/** Topbar — the user menu's disclosure caret. */
export function CaretDownIcon(props: NavIconProps) {
  return (
    <Glyph {...props}>
      <path d="M5.5 9h13L12 16.5z" fill="currentColor" stroke="none" />
    </Glyph>
  )
}

/** Row affordance — chevron. */
export function ChevronRightIcon(props: NavIconProps) {
  return (
    <Glyph {...props}>
      <path d="m9.5 5.5 7 6.5-7 6.5" />
    </Glyph>
  )
}

/** Collapse control — chevron pointing back at the rail. */
export function ChevronLeftIcon(props: NavIconProps) {
  return (
    <Glyph {...props}>
      <path d="m14.5 5.5-7 6.5 7 6.5" />
    </Glyph>
  )
}

/** Topbar — global search. */
export function SearchIcon(props: NavIconProps) {
  return (
    <Glyph {...props}>
      <circle cx="10.8" cy="10.8" r="6.3" />
      <path d="m15.5 15.5 4 4" />
    </Glyph>
  )
}

/** Topbar — help. */
export function HelpCircleIcon(props: NavIconProps) {
  return (
    <Glyph {...props}>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M9.8 9.4a2.3 2.3 0 1 1 3.4 2c-.8.5-1.2 1-1.2 1.9" />
      <circle cx="12" cy="16.4" r="0.9" fill="currentColor" stroke="none" />
    </Glyph>
  )
}

/** Topbar — notifications. */
export function BellIcon(props: NavIconProps) {
  return (
    <Glyph {...props}>
      <path d="M18 15.4V10.6a6 6 0 1 0-12 0v4.8L4.4 17.6h15.2z" />
      <path d="M9.6 20.2a2.6 2.6 0 0 0 4.8 0" />
    </Glyph>
  )
}

/** Collapse / expand control. */
export function MenuIcon(props: NavIconProps) {
  return (
    <Glyph strokeWidth={2} {...props}>
      <path d="M4 6.5h16M4 12h16M4 17.5h16" />
    </Glyph>
  )
}
